/** @jsx jsx */
import { jsx } from '@emotion/core';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import BackToTop from '../../ui/BackToTop';
import { Container, Row, Column } from '../../../styles/Grid';
import { fieldHasValue, fillEmptyValues } from '../../../utils/sitecore';
import ProgressBar from '../../ui/ProgressBar';
import { ParentDimensionsProps } from './commonPropTypes';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  onlyLgAndUpCss,
  stickyParentCss,
  stickyContainerCss,
  headlineCss,
  sectionCss,
  subsectionCss,
  textContainerCss,
} from './index.styles';

const topOpacity = {
  from: 0,
  to: 200,
};

const bottomOpacity = {
  from: -300,
  to: -600,
};

/**
 * Calculates the opacity to apply to the sticky component based on the progress
 * in the layout sticky component. It calculates the animation for top and bottom
 * in the same function since it's easier to control.
 * @param {object} parentDimensions Dimensions coming from the layout sticky container.
 */
export function getOpacityCss(parentDimensions) {
  const { top, bottom } = parentDimensions;
  const applyTopOpacity = top <= topOpacity.to;
  const opacityOptions = applyTopOpacity ? topOpacity : bottomOpacity;
  const value = applyTopOpacity ? top : bottom;
  const { from, to } = opacityOptions;
  const valuePercentage = Math.max(
    Math.min((value - from) / (to - from), 1),
    0,
  );

  return {
    opacity: applyTopOpacity ? valuePercentage : 1 - valuePercentage,
  };
}

class StickyDesktop extends SafePureComponent {
  safeRender() {
    const { rendering, parentDimensions } = this.props;
    const fields = fillEmptyValues(rendering.fields, {
      Headline: '',
      Section: '',
      Subsection: '',
    });
    const opacityCss = getOpacityCss(parentDimensions);

    return (
      <div css={[stickyParentCss, onlyLgAndUpCss, opacityCss]}>
        <Container css={stickyContainerCss}>
          <Row>
            <Column cols={{ lg: 2 }}>
              <ProgressBar percentage={parentDimensions.scrolledPercentage} />
              <div css={textContainerCss}>
                {fieldHasValue(fields.Headline) && (
                  <div css={headlineCss}>{get(fields, 'Headline.value')}</div>
                )}
                {fieldHasValue(fields.Section) && (
                  <div css={sectionCss}>{get(fields, 'Section.value')}</div>
                )}
                {fieldHasValue(fields.Subsection) && (
                  <div css={subsectionCss}>
                    {get(fields, 'Subsection.value')}
                  </div>
                )}
              </div>
              <BackToTop />
            </Column>
          </Row>
        </Container>
      </div>
    );
  }
}

StickyDesktop.displayName = 'StickyDesktop';

StickyDesktop.defaultProps = {
  parentDimensions: {
    top: 0,
    bottom: 0,
    scrolledPercentage: 0,
  },
};

StickyDesktop.propTypes = {
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      Headline: PropTypes.object,
      Section: PropTypes.object,
      Subsection: PropTypes.object,
    }),
  }),
  parentDimensions: ParentDimensionsProps,
};

export default StickyDesktop;
